/**
 * Задания дня и недели (ТЗ FR-GM-07).
 *
 * Задание отвечает на вопрос «что сделать сегодня», а накопительный счётчик —
 * на вопрос «сколько всего». Это разные вопросы, поэтому заданиям нужен
 * журнал по дням, а не сумма за всё время.
 *
 * Ни одно задание не отнимает сделанного и не торопит: недельные считаются по
 * последним семи дням, а не по календарной неделе, и воскресный вечер ничего
 * не обнуляет (ТЗ FR-GM-09).
 */

import type { DayActivity, QuestDefinition, QuestStatus } from '../contracts/achievements.js';
import { CLEAN_ACCURACY, QUESTS, WEEK_DAYS } from '../contracts/achievements.js';
import type { Workspace } from '../contracts/workspace.js';

const DAY_MS = 86_400_000;

/** Календарная дата без времени. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

/** Дни журнала, попадающие в срок задания. */
function daysInPeriod(
  log: readonly DayActivity[],
  definition: QuestDefinition,
  todayIso: string,
): DayActivity[] {
  const today = Date.parse(`${dayOf(todayIso)}T00:00:00.000Z`);
  const span = definition.period === 'day' ? 1 : WEEK_DAYS;

  return log.filter((entry) => {
    const date = Date.parse(`${entry.date}T00:00:00.000Z`);
    const distance = today - date;
    // Будущие даты не учитываются: переведённые назад часы не должны
    // засчитывать занятия, которых ещё не было.
    return distance >= 0 && distance < span * DAY_MS;
  });
}

/** Достигнутое значение задания. */
function measure(days: readonly DayActivity[], definition: QuestDefinition): number {
  switch (definition.id) {
    case 'daily-session':
      return days.reduce((total, entry) => total + entry.sessions, 0);
    case 'daily-characters':
    case 'weekly-characters':
      return days.reduce((total, entry) => total + entry.characters, 0);
    case 'daily-accuracy':
      return days.reduce((best, entry) => Math.max(best, entry.bestAccuracy), 0);
    case 'weekly-days':
      return days.filter((entry) => entry.sessions > 0).length;
    case 'weekly-accuracy':
      return days.filter((entry) => entry.bestAccuracy >= CLEAN_ACCURACY).length;
  }
}

/**
 * Состояние всех заданий на указанный день.
 *
 * @param workspace пространство с журналом занятий;
 * @param todayIso момент, на который считаются задания.
 */
export function questProgress(workspace: Workspace, todayIso: string): readonly QuestStatus[] {
  return QUESTS.map((definition) => {
    const current = measure(daysInPeriod(workspace.activityLog, definition, todayIso), definition);

    return {
      id: definition.id,
      period: definition.period,
      target: definition.target,
      current,
      done: current >= definition.target,
    };
  });
}
